import useSWR from 'swr';

import { fetchEndpointData } from '@utils/fetchEndpointData';
import type { SelfBillKind } from 'src/types/enums';

import type {
  SelfBillsResponse,
  SelfBillDetail,
  SupplierSelfBillStatusValue,
  ApproveOutcome,
  ApproveOutcomeList,
  PdfGenerationJobResponse,
  GenerateSelfBillingsResponse,
  DiscardSelfBillDraftResponse,
  DiscardSelfBillDraftsResponse,
  SelfBillIdResponse,
  SelfBillsExportResponse,
  ConvertSelfBillsToPurchasesResponse,
} from './endpoints/SelfBill.types';
import type { PeppolDelivery, SendPeppolResponse } from '../peppol/peppol.types';

export type SelfBillListEntry = SelfBillsResponse[number];

export const SELF_BILL_SWR_KEY = 'self-bill';

export interface SelfBillsFilters {
  // Ride overviews and self-billing invoices are listed on separate pages.
  kind: SelfBillKind;
  search?: string;
  // String enum name ('Draft' | 'Sent' | 'Failed'); maps 1:1 to the GraphQL
  // SupplierSelfBillStatus filter. Omit for "all statuses".
  status?: SupplierSelfBillStatusValue | null;
}

function buildSelfBillsParams(
  cursor: string | null | undefined,
  take: number | undefined,
  filters: SelfBillsFilters,
  sortBy?: string,
  sortOrder?: string,
): string {
  const params = new URLSearchParams();
  params.set('kind', filters.kind);
  if (cursor) params.set('id', cursor);
  if (take != null) params.set('take', take.toString());
  if (filters.search) params.set('search', filters.search);
  if (filters.status) params.set('status', filters.status);
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);
  return params.toString();
}

// ---- Queries ----

/** GET /v1/self-bills — mirrors GraphQL supplierSelfBills (list, cursor + filters). */
export async function getSelfBills(
  cursor: string | null | undefined,
  take: number | undefined,
  filters: SelfBillsFilters,
  sortBy?: string,
  sortOrder?: string,
): Promise<SelfBillsResponse> {
  const qs = buildSelfBillsParams(cursor, take, filters, sortBy, sortOrder);
  return fetchEndpointData<SelfBillsResponse>(`/api/v1/self-bills?${qs}`);
}

/** GET /v1/self-bills/:id — mirrors GraphQL supplierSelfBill. */
export async function getSelfBill(id: string): Promise<SelfBillDetail> {
  return fetchEndpointData<SelfBillDetail>(`/api/v1/self-bills/${id}`);
}

export function useSelfBill(id: string) {
  return useSWR([SELF_BILL_SWR_KEY, id], () => getSelfBill(id));
}

// ---- Mutations (async fetchers — callers handle isLoading/try-catch) ----

/** POST /v1/self-bills/:id/approve — mirrors GraphQL approveSelfBill. */
export async function approveSelfBill(selfBillingId: string, andSend?: boolean): Promise<ApproveOutcome> {
  return fetchEndpointData<ApproveOutcome>(`/api/v1/self-bills/${selfBillingId}/approve`, {
    method: 'POST',
    body: { andSend },
  });
}

/** POST /v1/self-bills/approve-bulk — mirrors GraphQL approveSelfBills. */
export async function approveSelfBills(selfBillingIds: string[], andSend?: boolean): Promise<ApproveOutcomeList> {
  return fetchEndpointData<ApproveOutcomeList>('/api/v1/self-bills/approve-bulk', {
    method: 'POST',
    body: { selfBillingIds, andSend },
  });
}

/** POST /v1/self-bills/:id/retry — mirrors GraphQL retrySelfBill. */
export async function retrySelfBill(selfBillingId: string): Promise<ApproveOutcome> {
  return fetchEndpointData<ApproveOutcome>(`/api/v1/self-bills/${selfBillingId}/retry`, {
    method: 'POST',
  });
}

/** POST /v1/self-bills/:id/send-peppol — manual push to Billit/Peppol. */
export async function sendSelfBillViaPeppol(selfBillingId: string): Promise<SendPeppolResponse> {
  return fetchEndpointData<SendPeppolResponse>(`/api/v1/self-bills/${selfBillingId}/send-peppol`, {
    method: 'POST',
  });
}

/** POST /v1/self-bills/:id/refresh-peppol-status — fetch the delivery status from Billit now. */
export async function refreshSelfBillPeppolStatus(selfBillingId: string): Promise<PeppolDelivery> {
  return fetchEndpointData<PeppolDelivery>(`/api/v1/self-bills/${selfBillingId}/refresh-peppol-status`, {
    method: 'POST',
  });
}

/** POST /v1/self-bills/:id/send — mirrors GraphQL sendSelfBill (returns { id }). */
export async function sendSelfBill(selfBillingId: string, contacts: number[]): Promise<SelfBillIdResponse> {
  return fetchEndpointData<SelfBillIdResponse>(`/api/v1/self-bills/${selfBillingId}/send`, {
    method: 'POST',
    body: { contacts },
  });
}

/** POST /v1/self-bills/:id/generate — mirrors GraphQL generateSelfBill (returns job ref). */
export async function generateSelfBill(selfBillingId: string): Promise<PdfGenerationJobResponse> {
  return fetchEndpointData<PdfGenerationJobResponse>(`/api/v1/self-bills/${selfBillingId}/generate`, {
    method: 'POST',
  });
}

/** POST /v1/self-bills/generate — mirrors GraphQL generateSelfBillings. */
export async function generateSelfBillings(): Promise<GenerateSelfBillingsResponse> {
  return fetchEndpointData<GenerateSelfBillingsResponse>('/api/v1/self-bills/generate', {
    method: 'POST',
  });
}

/** POST /v1/self-bills/convert-to-purchases — bulk AKF conversion action. */
export async function convertSelfBillsToPurchases(
  selfBillingIds: string[],
): Promise<ConvertSelfBillsToPurchasesResponse> {
  return fetchEndpointData<ConvertSelfBillsToPurchasesResponse>('/api/v1/self-bills/convert-to-purchases', {
    method: 'POST',
    body: { selfBillingIds },
  });
}

/** DELETE /v1/self-bills/:id — mirrors GraphQL discardSelfBillDraft. */
export async function discardSelfBillDraft(selfBillingId: string): Promise<DiscardSelfBillDraftResponse> {
  return fetchEndpointData<DiscardSelfBillDraftResponse>(`/api/v1/self-bills/${selfBillingId}`, {
    method: 'DELETE',
  });
}

/** POST /v1/self-bills/discard-bulk — mirrors GraphQL discardSelfBillDrafts (returns { count }). */
export async function discardSelfBillDrafts(selfBillingIds: string[]): Promise<DiscardSelfBillDraftsResponse> {
  return fetchEndpointData<DiscardSelfBillDraftsResponse>('/api/v1/self-bills/discard-bulk', {
    method: 'POST',
    body: { selfBillingIds },
  });
}

/** DELETE /v1/self-bills/:id/orders/:orderId — mirrors GraphQL removeOrderFromSelfBill (returns { id }). */
export async function removeOrderFromSelfBill(selfBillingId: string, orderId: string): Promise<SelfBillIdResponse> {
  return fetchEndpointData<SelfBillIdResponse>(`/api/v1/self-bills/${selfBillingId}/orders/${orderId}`, {
    method: 'DELETE',
  });
}

/** POST /v1/self-bills/:id/orders/:orderId/exclude — removes the order and keeps it out of later runs. */
export async function excludeOrderFromSelfBill(selfBillingId: string, orderId: string): Promise<SelfBillIdResponse> {
  return fetchEndpointData<SelfBillIdResponse>(`/api/v1/self-bills/${selfBillingId}/orders/${orderId}/exclude`, {
    method: 'POST',
  });
}

/** POST /v1/self-bills/export — mirrors GraphQL exportSelfBills (returns the two XML strings). */
export async function exportSelfBills(body: {
  startInvoiceNumber: string;
  endInvoiceNumber: string;
  financialPeriod: number;
}): Promise<SelfBillsExportResponse> {
  return fetchEndpointData<SelfBillsExportResponse>('/api/v1/self-bills/export', {
    method: 'POST',
    body,
  });
}
